/**
 * Pending Results (Resultados Pendentes) — admin diagnostic page.
 *
 * Reads the Result Outbox from the SAME browser's localStorage (so each
 * computer in a school's IT lab will see its OWN locally-queued submissions
 * — the outbox is intentionally device-local for privacy and to avoid a
 * server-side global "lost results" table).
 *
 * Capabilities:
 *  - List every queued / expired entry with full payload + last error.
 *  - "Reenviar agora" button forces an immediate drain bypassing backoff.
 *  - "Reenviar este" button retries a single entry (useful when one is
 *    stuck and the others are healthy).
 *  - "Eliminar" button drops an entry (e.g. duplicate, test data).
 *  - "Limpar synced/expired" + "Limpar tudo" bulk operations.
 *  - Live updates via the subscribe() hook of resultOutbox.
 */
import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CloudOff, RefreshCw, Trash2, AlertTriangle, ShieldCheck, Inbox } from "lucide-react";
import {
  subscribe,
  getOutbox,
  forceDrain,
  removeEntry,
  clearOutbox,
  submitResult,
} from "@/lib/resultOutbox";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  expired: "bg-rose-100 text-rose-800 border-rose-300",
  synced:  "bg-emerald-100 text-emerald-800 border-emerald-300",
};

function fmtTimestamp(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  return d.toLocaleString("pt-PT", { dateStyle: "short", timeStyle: "medium" });
}

function PayloadPre({ payload }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[11px] font-bold text-sky-700 hover:text-sky-900"
        data-testid="toggle-payload"
      >
        {open ? "− Ocultar payload" : "+ Ver payload"}
      </button>
      {open && (
        <pre className="mt-1 p-2 bg-slate-900 text-emerald-200 text-[10px] rounded-lg overflow-auto max-h-48">
{JSON.stringify(payload, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function PendingResults() {
  const [entries, setEntries] = useState(() => getOutbox());
  useEffect(() => subscribe(setEntries), []);

  const pending = entries.filter((e) => e.status === "pending");
  const expired = entries.filter((e) => e.status === "expired");

  const handleForceDrain = async () => {
    toast.info("A tentar reenviar todos os resultados pendentes…");
    await forceDrain();
    toast.success("Drenagem concluída");
  };

  const handleResendOne = async (entry) => {
    toast.info(`A reenviar “${entry.description || entry.endpoint}”…`);
    const result = await submitResult(entry.endpoint, entry.payload, {
      description: entry.description,
    });
    // Whether the immediate POST succeeded or not, the duplicate entry needs
    // cleanup — submitResult attaches the SAME client_submission_id (already
    // in payload), so the new entry (if queued) replaces this one logically.
    removeEntry(entry.id);
    if (result.queued) {
      toast.warning("Ainda em fila — será tentado novamente automaticamente");
    } else if (result.ok) {
      toast.success("Resultado entregue!");
    }
  };

  const handleClearTerminal = () => {
    if (!window.confirm("Eliminar todos os resultados expirados?")) return;
    const remaining = entries.filter((e) => e.status !== "expired");
    clearOutbox();
    remaining.forEach(() => {/* removed */});
    // Re-add the survivors (clearOutbox wipes everything).
    if (remaining.length > 0) {
      // Brute-force reinsert via localStorage to preserve original IDs/timestamps.
      window.localStorage.setItem("supert_result_outbox", JSON.stringify(remaining));
      // Trigger a fake event so subscribers re-read.
      window.dispatchEvent(new StorageEvent("storage", { key: "supert_result_outbox" }));
    }
    toast.success("Expirados removidos");
  };

  const handleClearAll = () => {
    if (!window.confirm("ATENÇÃO: eliminar TUDO incluindo resultados pendentes?")) return;
    clearOutbox();
    toast.success("Outbox limpa");
  };

  return (
    <div className="space-y-4" data-testid="pending-results-page">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="w-5 h-5" /> Resultados Pendentes (Outbox local)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed">
            Esta página lista os resultados de jogo que <strong>este computador</strong> ainda não
            conseguiu enviar para o servidor — tipicamente por instabilidade da ligação WiFi durante
            uma competição escolar. O sistema tenta automaticamente a cada 5 segundos. Cada
            resultado tem um <em>client_submission_id</em> único, pelo que reenvios <strong>nunca</strong> criam
            duplicados na base de dados.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className={STATUS_COLORS.pending}>
              <CloudOff className="w-3 h-3 mr-1" /> Pendentes: {pending.length}
            </Badge>
            <Badge variant="outline" className={STATUS_COLORS.expired}>
              <AlertTriangle className="w-3 h-3 mr-1" /> Expirados (24h): {expired.length}
            </Badge>
            <span className="flex-1" />
            <Button
              variant="default"
              size="sm"
              onClick={handleForceDrain}
              disabled={pending.length === 0}
              data-testid="btn-force-drain"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> Reenviar todos
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearTerminal}
              disabled={expired.length === 0}
              data-testid="btn-clear-expired"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Limpar expirados
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={handleClearAll}
              disabled={entries.length === 0}
              data-testid="btn-clear-all"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Limpar tudo
            </Button>
          </div>
        </CardContent>
      </Card>

      {entries.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <ShieldCheck className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
            <div className="font-bold text-slate-700">Tudo entregue.</div>
            <div className="text-sm">Não há resultados pendentes neste dispositivo.</div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="pending-results-list">
          {entries.map((e) => (
            <Card key={e.id} data-testid={`pending-entry-${e.id}`}>
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Badge variant="outline" className={STATUS_COLORS[e.status] || ""}>
                    {e.status}
                  </Badge>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-800 truncate">
                      {e.description || e.endpoint}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="font-mono">{e.endpoint}</span>
                      {" · "}
                      Tentativas: <strong>{e.attempts}</strong>
                      {" · "}1ª: {fmtTimestamp(e.firstAttemptAt)}
                      {" · "}Última: {fmtTimestamp(e.lastAttemptAt)}
                      {e.status === "pending" && (
                        <>{" · "}Próxima: {fmtTimestamp(e.nextAttemptAt)}</>
                      )}
                    </div>
                    {e.lastError && (
                      <div className="text-xs text-rose-600 mt-1">
                        <strong>Último erro:</strong> {e.lastError}
                      </div>
                    )}
                    <div className="text-[10px] text-slate-400 mt-0.5 font-mono break-all">
                      id: {e.id}
                    </div>
                    <PayloadPre payload={e.payload} />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleResendOne(e)}
                      data-testid={`btn-resend-${e.id}`}
                    >
                      <RefreshCw className="w-3.5 h-3.5 mr-1" /> Reenviar
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { removeEntry(e.id); toast("Removido"); }}
                      data-testid={`btn-remove-${e.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
